// Banner "Có bản cập nhật mới" — chỉ hiện trong native app khi version app
// cũ hơn LATEST_MOBILE_APP_VERSION backend trả về, và user có role vận hành
// (super_admin/organizer/referee/publisher). Giúp admin/trọng tài/publisher
// không bao giờ test nhầm bản cũ.
import { useEffect, useState } from "react";
import { ArrowsClockwise, X } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";

const PRIVILEGED = ["super_admin", "organizer", "referee", "publisher"];
// v17.38 — Dismiss BỀN VỮNG theo `latest_version` (không phải session).
// User (đặc biệt internal tester) đang trên track cũ 1.0.18 mà BE ads 1.0.22
// → bấm X 1 lần là ẩn đến khi BE ads version NEWER 1.0.22. Nếu bump 1.0.23
// thì banner hiện lại → user nhận thông báo update mới.
const DISMISS_KEY = "pl.appUpdateBanner.dismissedVersion";

function isOlder(current, latest) {
  const a = String(current).split(".").map((n) => parseInt(n, 10) || 0);
  const b = String(latest).split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] || 0) < (b[i] || 0)) return true;
    if ((a[i] || 0) > (b[i] || 0)) return false;
  }
  return false;
}

export default function AppUpdateBanner() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  // v17.38 — Đọc dismissedVersion từ localStorage (không phải session).
  const [dismissedVersion, setDismissedVersion] = useState(() => {
    try { return window.localStorage.getItem(DISMISS_KEY) || ""; } catch { return ""; }
  });

  const nativeVersion =
    typeof window !== "undefined" ? window.__pickleagueNativeAppVersion : null;
  const platform =
    typeof window !== "undefined" ? window.__pickleagueNativePlatform : null;
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const privileged = PRIVILEGED.some((r) => roles.includes(r));
  const eligible = !!nativeVersion && !!platform && privileged;

  useEffect(() => {
    if (!eligible || cfg) return;
    // v17.37 — Truyền platform (khớp với AppUpdateGate v17.36) để tránh BE
    // fallback về Android version cho iOS device → false-positive banner.
    api
      .get(`/mobile/app-version?platform=${encodeURIComponent(platform)}`)
      .then((res) => setCfg(res.data || null))
      .catch(() => {});
  }, [eligible, cfg, platform]);

  const latest = cfg?.latest_version;
  if (!eligible || !latest || !isOlder(nativeVersion, latest)) return null;
  // v17.38 — Nếu user đã dismiss ĐÚNG version này rồi → ẩn. Chỉ hiện lại khi
  // BE bump version cao hơn cái đã dismiss.
  if (dismissedVersion && !isOlder(dismissedVersion, latest)) return null;
  // Phase 91 — force update đang bật → AppUpdateGate đã chặn toàn màn hình,
  // không cần hiện banner trùng lặp.
  if (cfg?.force_update) return null;
  // v17.39 — Banner chỉ hiện khi admin explicit bật `banner_enabled=true`.
  // Mặc định OFF: APK cũ inject sai version → banner báo nhầm cho user đã update.
  if (!cfg?.banner_enabled) return null;

  return (
    <div
      className="pl-safe-pt fixed top-0 inset-x-0 z-[95] px-3 pt-2"
      data-testid="app-update-banner"
    >
      <div className="max-w-2xl mx-auto flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl bg-[#1A1206]/97 backdrop-blur-xl border border-[#FF8A00]/50 shadow-[0_6px_24px_rgba(255,138,0,0.25)]">
        <ArrowsClockwise size={16} weight="bold" className="text-[#FF8A00] shrink-0" />
        <div className="flex-1 min-w-0 text-[12px] leading-snug">
          <span className="font-black text-[#FF8A00]">Có bản cập nhật mới v{latest}</span>
          <span className="text-white/75"> — bạn đang dùng v{nativeVersion}. Đóng hẳn app rồi mở lại để nhận bản cập nhật.</span>
        </div>
        <button
          type="button"
          onClick={() => {
            // v17.38 — Ghi ĐÚNG version đang được advertise vào localStorage.
            // Hiện lại chỉ khi BE bump version cao hơn (isOlder(dismissed, newLatest)=true).
            try { window.localStorage.setItem(DISMISS_KEY, latest); } catch { /* noop */ }
            setDismissedVersion(latest);
          }}
          className="w-7 h-7 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center shrink-0"
          data-testid="app-update-banner-close"
          aria-label="Đóng cảnh báo cập nhật"
        >
          <X size={12} weight="bold" className="text-white/70" />
        </button>
      </div>
    </div>
  );
}
